import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import SearchableSelect from '@/components/SearchableSelect';
import { Input } from '@/components/ui/input';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer,
} from 'recharts';
import {
  TrendUp, Receipt, Users, Ear, Stethoscope, Percent, CurrencyInr, ChartLineUp, ChartBar,
} from '@phosphor-icons/react';

const PRIMARY = '#0F4C81';
const ACCENT = '#14B8A6';

const fmtINR = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (Math.abs(v) >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  if (Math.abs(v) >= 1e3) return `₹${(v / 1e3).toFixed(1)}K`;
  return `₹${v.toFixed(0)}`;
};
const fmtFull = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN');
const monthLabel = (m) => {
  try { const [y, mo] = m.split('-'); return new Date(y, mo - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }); }
  catch { return m; }
};

const METRIC_CARDS = [
  { key: 'revenue', label: 'Net Revenue', icon: CurrencyInr, fmt: fmtFull, color: PRIMARY },
  { key: 'invoices', label: 'Invoices', icon: Receipt, fmt: fmtNum, color: '#6366F1' },
  { key: 'customers', label: 'Customers', icon: Users, fmt: fmtNum, color: '#0EA5E9' },
  { key: 'ha_units', label: 'HA Units Sold', icon: Ear, fmt: fmtNum, color: ACCENT },
  { key: 'asp', label: 'ASP (per HA)', icon: TrendUp, fmt: fmtFull, color: '#F59E0B' },
  { key: 'diag_count', label: 'Diagnostics', icon: Stethoscope, fmt: fmtNum, color: '#EC4899' },
  { key: 'discount_pct', label: 'Avg Discount', icon: Percent, fmt: (v) => `${Number(v || 0).toFixed(1)}%`, color: '#EF4444' },
  { key: 'avg_ticket', label: 'Avg Ticket', icon: Receipt, fmt: fmtFull, color: '#8B5CF6' },
];

export default function ClinicTrendsPage() {
  const [clinic, setClinic] = useState('');
  const [clinics, setClinics] = useState([]);
  const [preset, setPreset] = useState(6);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [view, setView] = useState('trend');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { clinic };
      if (preset === 'custom' && customStart && customEnd) {
        params.start_date = customStart;
        params.end_date = customEnd;
      } else if (preset !== 'custom') {
        params.months = preset;
      }
      const { data: res } = await api.get('/analytics/clinic-trends', { params });
      setData(res);
      if (res.clinics?.length) setClinics(res.clinics);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [clinic, preset, customStart, customEnd]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const trend = (data?.trend || []).map(r => ({ ...r, label: monthLabel(r.month) }));
  const compare = data?.compare || [];
  const devices = data?.top_devices || [];

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="clinic-trends-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2">
            <TrendUp size={26} weight="bold" className="text-[#0F4C81]" /> Clinic-wise Trends
          </h1>
          <p className="text-sm text-[#64748B] mt-1">How each clinic is moving — revenue, customers, Hearing Aids vs Diagnostics, ASP &amp; discounts. Amounts are net of discount, pre-GST.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 mb-6 flex flex-wrap items-end gap-4" data-testid="ct-filters">
        <div>
          <label className="text-[11px] text-[#64748B] block mb-1">Clinic</label>
          <SearchableSelect
            value={clinic}
            onChange={setClinic}
            options={[{ value: '', label: 'All Clinics (combined)' }, ...clinics.map(c => ({ value: c, label: c }))]}
            placeholder="All Clinics"
            testid="ct-clinic-select"
            buttonClassName="w-64"
          />
        </div>
        <div>
          <label className="text-[11px] text-[#64748B] block mb-1">Period</label>
          <div className="flex items-center gap-1 bg-[#F1F5F9] rounded-lg p-1">
            {[3, 6, 12].map(p => (
              <button key={p} onClick={() => setPreset(p)} data-testid={`ct-preset-${p}`}
                className={`px-3 h-8 rounded-md text-xs font-medium transition-colors ${preset === p ? 'bg-white text-[#0F4C81] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`}>
                {p}M
              </button>
            ))}
            <button onClick={() => setPreset('custom')} data-testid="ct-preset-custom"
              className={`px-3 h-8 rounded-md text-xs font-medium transition-colors ${preset === 'custom' ? 'bg-white text-[#0F4C81] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`}>
              Custom
            </button>
          </div>
        </div>
        {preset === 'custom' && (
          <div className="flex items-end gap-2">
            <div>
              <label className="text-[11px] text-[#64748B] block mb-1">From</label>
              <Input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="h-10 w-40" data-testid="ct-start" />
            </div>
            <div>
              <label className="text-[11px] text-[#64748B] block mb-1">To</label>
              <Input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="h-10 w-40" data-testid="ct-end" />
            </div>
          </div>
        )}
        <div className="ml-auto flex items-center gap-1 bg-[#F1F5F9] rounded-lg p-1">
          <button onClick={() => setView('trend')} data-testid="ct-view-trend"
            className={`px-3 h-8 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ${view === 'trend' ? 'bg-white text-[#0F4C81] shadow-sm' : 'text-[#64748B]'}`}>
            <ChartLineUp size={14} /> Trend
          </button>
          <button onClick={() => setView('compare')} data-testid="ct-view-compare"
            className={`px-3 h-8 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ${view === 'compare' ? 'bg-white text-[#0F4C81] shadow-sm' : 'text-[#64748B]'}`}>
            <ChartBar size={14} /> Compare Clinics
          </button>
        </div>
      </div>

      {loading && <div className="py-20 text-center text-sm text-[#64748B]"><div className="inline-block w-6 h-6 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" /><p className="mt-2">Crunching numbers…</p></div>}

      {!loading && data && (
        <>
          {/* Summary cards (selection totals) */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="ct-summary-cards">
            {METRIC_CARDS.map(c => {
              const Icon = c.icon;
              return (
                <div key={c.key} className="bg-white border border-[#E2E8F0] rounded-xl p-4 hover:shadow-md transition-shadow" data-testid={`ct-card-${c.key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] uppercase tracking-wide text-[#94A3B8] font-medium">{c.label}</span>
                    <Icon size={16} weight="bold" style={{ color: c.color }} />
                  </div>
                  <p className="text-xl font-bold text-[#0F172A]">{c.fmt(totals[c.key])}</p>
                </div>
              );
            })}
          </div>

          {view === 'trend' ? (
            <>
              {/* Trend chart */}
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 mb-6">
                <h3 className="text-sm font-semibold text-[#0F172A] mb-4">{clinic || 'All Clinics'} — Monthly Movement</h3>
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={trend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F6" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748B' }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={fmtINR} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#64748B' }} />
                    <Tooltip formatter={(val, name) => name === 'Net Revenue' ? fmtFull(val) : fmtNum(val)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line yAxisId="left" type="monotone" dataKey="revenue" name="Net Revenue" stroke={PRIMARY} strokeWidth={2.5} dot={{ r: 3 }} />
                    <Line yAxisId="right" type="monotone" dataKey="ha_units" name="HA Units" stroke={ACCENT} strokeWidth={2.5} dot={{ r: 3 }} />
                    <Line yAxisId="right" type="monotone" dataKey="customers" name="Customers" stroke="#F59E0B" strokeWidth={2} dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Trend table */}
              <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden mb-6">
                <table className="w-full text-sm" data-testid="ct-trend-table">
                  <thead className="bg-[#F8FAFC] text-[#64748B] text-xs">
                    <tr>
                      <th className="text-left p-3 font-medium">Month</th>
                      <th className="text-right p-3 font-medium">Net Revenue</th>
                      <th className="text-right p-3 font-medium">Invoices</th>
                      <th className="text-right p-3 font-medium">Customers</th>
                      <th className="text-right p-3 font-medium">HA Units</th>
                      <th className="text-right p-3 font-medium">ASP</th>
                      <th className="text-right p-3 font-medium">Diagnostics</th>
                      <th className="text-right p-3 font-medium">Discount %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trend.map(r => (
                      <tr key={r.month} className="border-t border-[#F1F5F9] hover:bg-[#F8FAFC]">
                        <td className="p-3 font-medium text-[#0F172A]">{r.label}</td>
                        <td className="p-3 text-right">{fmtFull(r.revenue)}</td>
                        <td className="p-3 text-right">{fmtNum(r.invoices)}</td>
                        <td className="p-3 text-right">{fmtNum(r.customers)}</td>
                        <td className="p-3 text-right">{fmtNum(r.ha_units)}</td>
                        <td className="p-3 text-right">{fmtFull(r.asp)}</td>
                        <td className="p-3 text-right">{fmtNum(r.diag_count)}</td>
                        <td className="p-3 text-right">{r.discount_pct}%</td>
                      </tr>
                    ))}
                    {trend.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-[#94A3B8]">No data for this period</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              {/* Compare chart */}
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 mb-6">
                <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Clinic Comparison — Net Revenue ({data?.period?.start} → {data?.period?.end})</h3>
                <ResponsiveContainer width="100%" height={Math.max(320, compare.length * 28)}>
                  <BarChart data={compare} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F6" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={fmtINR} />
                    <YAxis type="category" dataKey="clinic" width={180} tick={{ fontSize: 10, fill: '#475569' }} />
                    <Tooltip formatter={(val) => fmtFull(val)} />
                    <Bar dataKey="revenue" name="Net Revenue" fill={PRIMARY} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Compare table */}
              <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden mb-6">
                <table className="w-full text-sm" data-testid="ct-compare-table">
                  <thead className="bg-[#F8FAFC] text-[#64748B] text-xs">
                    <tr>
                      <th className="text-left p-3 font-medium">Clinic</th>
                      <th className="text-right p-3 font-medium">Net Revenue</th>
                      <th className="text-right p-3 font-medium">Invoices</th>
                      <th className="text-right p-3 font-medium">Customers</th>
                      <th className="text-right p-3 font-medium">HA Units</th>
                      <th className="text-right p-3 font-medium">ASP</th>
                      <th className="text-right p-3 font-medium">Diagnostics</th>
                      <th className="text-right p-3 font-medium">Discount %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compare.map(r => (
                      <tr key={r.clinic} className="border-t border-[#F1F5F9] hover:bg-[#F8FAFC] cursor-pointer" onClick={() => { setClinic(r.clinic); setView('trend'); }} data-testid={`ct-compare-row-${r.clinic}`}>
                        <td className="p-3 font-medium text-[#0F4C81]">{r.clinic}</td>
                        <td className="p-3 text-right">{fmtFull(r.revenue)}</td>
                        <td className="p-3 text-right">{fmtNum(r.invoices)}</td>
                        <td className="p-3 text-right">{fmtNum(r.customers)}</td>
                        <td className="p-3 text-right">{fmtNum(r.ha_units)}</td>
                        <td className="p-3 text-right">{fmtFull(r.asp)}</td>
                        <td className="p-3 text-right">{fmtNum(r.diag_count)}</td>
                        <td className="p-3 text-right">{r.discount_pct}%</td>
                      </tr>
                    ))}
                    {compare.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-[#94A3B8]">No data for this period</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Top devices */}
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="ct-top-devices">
            <h3 className="text-sm font-semibold text-[#0F172A] mb-4 flex items-center gap-2"><Ear size={16} className="text-[#14B8A6]" /> Top Hearing-Aid Models — {clinic || 'All Clinics'}</h3>
            {devices.length === 0 ? <p className="text-sm text-[#94A3B8]">No hearing-aid sales in this period.</p> : (
              <div className="space-y-2">
                {devices.map((d, i) => {
                  const max = devices[0].revenue || 1;
                  return (
                    <div key={d.name} className="flex items-center gap-3">
                      <span className="text-xs text-[#94A3B8] w-5">{i + 1}</span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-xs text-[#0F172A] font-medium truncate">{d.name}</span>
                          <span className="text-xs text-[#64748B] whitespace-nowrap ml-2">{d.units} units · {fmtFull(d.revenue)}</span>
                        </div>
                        <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                          <div className="h-full bg-[#14B8A6] rounded-full" style={{ width: `${Math.max(4, (d.revenue / max) * 100)}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
